import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { PRIMARY, BASE } from "../../utils/constants/colors";
import { fontSize, fontWeight } from "../../utils/constants/UI";
import { useNavigate } from "react-router-dom";
import { getRequest } from "../../Apis/apiWrapper";
import { paymentEndpoints } from "../../utils/constants/httpConstants";
import { PROFILE_PATH } from "../../utils/constants/routes";

// Persistent top banner shown only while the tenant is on a 7-day trial. Self-fetches the current
// subscription once on mount; renders nothing unless status === "trialing". While visible it adds
// the `riq-trial-banner` class to <body>, which offsets the app content (see index.css) so this
// fixed bar sits ABOVE the CustomHeader instead of overlapping it. Soft coral tint (not solid brand
// coral) to stay unobtrusive, matching the guided-tour banner's tinted-bar treatment.
const BANNER_HEIGHT = 40;
const useStyles = makeStyles({
  bannerRoot: { position: "fixed", top: 0, left: "5rem", right: 0, zIndex: 1101 },
  bannerWrap: {
    width: "100%",
    background: PRIMARY.W_50,
    borderBottom: `1px solid ${PRIMARY.W_200}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    height: `${BANNER_HEIGHT}px`,
    boxSizing: "border-box",
    gap: "16px",
  },
  copy: {
    fontSize: fontSize.SM,
    fontWeight: fontWeight.MEDIUM,
    color: PRIMARY.W_700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  manageBtn: {
    background: PRIMARY.W_500,
    color: BASE.WHITE,
    border: "none",
    borderRadius: "7px",
    padding: "5px 14px",
    fontSize: fontSize.SM,
    fontWeight: fontWeight.SEMIBOLD,
    lineHeight: "20px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "background 0.15s ease",
    "&:hover": { background: PRIMARY.W_600 },
  },
  right: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: PRIMARY.W_700,
    cursor: "pointer",
    padding: "0 4px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    borderRadius: "6px",
    fontSize: "20px",
    flexShrink: 0,
    transition: "background 0.15s ease",
    "&:hover": { background: PRIMARY.W_100 },
  },
});

const DISMISS_KEY = "riq_trial_banner_dismissed";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const TrialBanner = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null); // { daysLeft, chargeDate, amount }
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch (_) { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRequest(navigate, paymentEndpoints.currentSubscription);
        const data = res?.data?.data;
        const sub = data?.subscription;
        if (cancelled || !data?.has_subscription || sub?.status !== "trialing") return;
        // Cancelled trial: Stripe keeps status "trialing" until the trial ends, but the
        // banner's "you'll be charged $X" would be a lie — nothing will be charged. Hide it.
        if (data?.pending_cancellation || sub?.cancel_at_period_end) return;
        const end = sub?.trial_end || sub?.current_period_end;
        if (!end) return;
        const ms = new Date(end).getTime() - Date.now();
        const daysLeft = Math.max(0, Math.ceil(ms / 86400000));
        const amount = sub?.plan?.amount ? `$${Math.round(sub.plan.amount / 100)}` : "";
        setTrial({ daysLeft, chargeDate: fmtDate(end), amount });
      } catch (_) {
        /* silent — no banner if the subscription can't be read */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While the banner is on screen, flag <body> so the app content is offset down by BANNER_HEIGHT
  // (index.css) — this pushes the CustomHeader below the fixed bar instead of being overlapped.
  useEffect(() => {
    if (!trial || dismissed) return;
    document.body.classList.add("riq-trial-banner");
    return () => document.body.classList.remove("riq-trial-banner");
  }, [trial, dismissed]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setDismissed(true);
  };

  if (!trial || dismissed) return null;

  return ReactDOM.createPortal(
    <Box className={classes.bannerRoot}>
      <Box className={classes.bannerWrap}>
        <span className={classes.copy}>
          <b>Free trial</b> · {trial.daysLeft} day{trial.daysLeft === 1 ? "" : "s"} left
          {trial.amount ? ` — you'll be charged ${trial.amount} on ${trial.chargeDate}` : ""}
        </span>
        <Box className={classes.right}>
          <button
            className={classes.manageBtn}
            onClick={() => navigate(`${PROFILE_PATH}?tab=Billing`)}
          >
            Manage plan
          </button>
          <button
            className={classes.closeBtn}
            onClick={handleDismiss}
            aria-label="Dismiss trial banner"
            title="Dismiss"
          >
            &times;
          </button>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};

export default TrialBanner;
